/**
 * Enhanced DateTimeRange Component - Compact Version
 * A modern, compact date-time range picker with hours/minutes support.
 * Supports both vertical (for filter sidebars) and horizontal layouts.
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    IconButton,
    Collapse,
    Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ClearIcon from '@mui/icons-material/Clear';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './CustomDateRange.css';
import helpers from '../assets/js/helper';

// Styled Components - Compact Version
const GlassContainer = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '10px 12px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,1) 100%)',
    borderRadius: 12,
    border: '1px solid rgba(0,0,0,0.06)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease',
}));

const DateTimeField = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontSize: '9px',
    fontWeight: 600,
    color: '#64748b',
    fontFamily: 'Cairo, sans-serif',
    minWidth: 18,
    textAlign: 'center',
}));

const StyledInputWrapper = styled(Box)(({ focused }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 6px',
    borderRadius: 6,
    background: focused ? 'rgba(99,102,241,0.08)' : 'rgba(241,245,249,0.9)',
    border: `1px solid ${focused ? 'rgba(99,102,241,0.4)' : 'transparent'}`,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    flex: 1,
    minHeight: 24,
    '&:hover': {
        background: 'rgba(99,102,241,0.05)',
        borderColor: 'rgba(99,102,241,0.2)',
    },
}));

const TimeSelect = styled('select')(({ theme }) => ({
    appearance: 'none',
    background: '#e0e7ff',
    border: '1px solid #c7d2fe',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#4f46e5',
    cursor: 'pointer',
    outline: 'none',
    textAlign: 'center',
    minWidth: 44,
    '&:focus': {
        borderColor: '#6366f1',
    },
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
    width: 40,
    height: 22,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                opacity: 1,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: 18,
        height: 18,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    },
    '& .MuiSwitch-track': {
        borderRadius: 11,
        backgroundColor: '#cbd5e1',
        opacity: 1,
    },
}));

// Custom Time Input Component - Compact
const CustomTimeInput = ({ value, onChange }) => {
    const hours = value?.split(':')?.[0] || '00';
    const minutes = value?.split(':')?.[1] || '00';

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            p: 1,
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
        }}>
            <AccessTimeIcon sx={{ color: '#6366f1', fontSize: 16 }} />
            <Typography sx={{ fontSize: '10px', color: '#64748b', fontWeight: 500 }}>الوقت:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <TimeSelect
                    value={hours}
                    onChange={(e) => onChange(`${e.target.value.padStart(2, '0')}:${minutes}`)}
                >
                    {[...Array(24).keys()].map((hour) => (
                        <option key={hour} value={hour.toString().padStart(2, '0')}>
                            {hour.toString().padStart(2, '0')}
                        </option>
                    ))}
                </TimeSelect>
                <Typography sx={{ fontWeight: 700, color: '#6366f1', fontSize: '12px' }}>:</Typography>
                <TimeSelect
                    value={minutes}
                    onChange={(e) => onChange(`${hours}:${e.target.value.padStart(2, '0')}`)}
                >
                    {[...Array(60).keys()].map((minute) => (
                        <option key={minute} value={minute.toString().padStart(2, '0')}>
                            {minute.toString().padStart(2, '0')}
                        </option>
                    ))}
                </TimeSelect>
            </Box>
        </Box>
    );
};

// Main Component
const CustomDateRange = ({
    dateRange,
    setDateRange,
    title,
    fromTitle,
    toTitle,
    horizontal = false,
    showTitle = true,
}) => {
    const [t] = useTranslation("common");
    const [fromFocused, setFromFocused] = useState(false);
    const [toFocused, setToFocused] = useState(false);

    const isAllTime = dateRange?.all_time === 1;

    const handleDateChange = (field, value) => {
        let datetimeString = null;
        try {
            const datetime = helpers.formatDateTime(value);
            datetimeString = datetime;
        } catch (error) { }

        if (field === 'from') {
            // If "to" date exists and new "from" is after "to", adjust "from" to match "to"
            if (dateRange?.creationDateTo && value && value > dateRange.creationDateTo) {
                value = new Date(dateRange.creationDateTo);
                try {
                    datetimeString = helpers.formatDateTime(value);
                } catch (error) { }
            }
            setDateRange({
                ...dateRange,
                creationDateFrom: value,
                creation_date_from: datetimeString
            });
        } else {
            // If "from" date exists and new "to" is before "from", adjust "to" to match "from"
            if (dateRange?.creationDateFrom && value && value < dateRange.creationDateFrom) {
                value = new Date(dateRange.creationDateFrom);
                try {
                    datetimeString = helpers.formatDateTime(value);
                } catch (error) { }
            }
            setDateRange({
                ...dateRange,
                creationDateTo: value,
                creation_date_to: datetimeString
            });
        }
    };

    const handleToggleAllTime = (checked) => {
        setDateRange({
            ...dateRange,
            all_time: checked ? 1 : 0
        });
    };

    const handleClear = (field, e) => {
        e.stopPropagation();
        if (field === 'from') {
            setDateRange({
                ...dateRange,
                creationDateFrom: null,
                creation_date_from: null
            });
        } else {
            setDateRange({
                ...dateRange,
                creationDateTo: null,
                creation_date_to: null
            });
        }
    };

    const formatDisplayDate = (date) => {
        if (!date) return null;
        try {
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            const timeStr = `${hours}:${minutes}`;
            return { dateStr, timeStr };
        } catch {
            return null;
        }
    };

    const DateInputButton = React.forwardRef(({ value, onClick, placeholder, field }, ref) => {
        const displayData = formatDisplayDate(field === 'from' ? dateRange?.creationDateFrom : dateRange?.creationDateTo);
        const isFocused = field === 'from' ? fromFocused : toFocused;

        return (
            <StyledInputWrapper
                ref={ref}
                onClick={onClick}
                focused={isFocused ? 1 : 0}
            >
                <CalendarMonthIcon sx={{ color: isFocused ? '#6366f1' : '#94a3b8', fontSize: 16 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {displayData ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                            <Typography sx={{ fontSize: '8px', fontWeight: 600, color: '#1e293b', fontFamily: 'monospace' }}>
                                {displayData.dateStr}
                            </Typography>
                            <Typography sx={{ fontSize: '8px', color: '#6366f1', fontWeight: 600, fontFamily: 'monospace' }}>
                                {displayData.timeStr}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '8px', color: '#94a3b8', fontFamily: 'Cairo' }}>
                            اختر
                        </Typography>
                    )}
                </Box>
                {displayData && (
                    <IconButton
                        size="small"
                        onClick={(e) => handleClear(field, e)}
                        sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                    >
                        <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                )}
            </StyledInputWrapper>
        );
    });

    return (
        <GlassContainer elevation={0}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: isAllTime ? 0 : 0.5,
                borderBottom: isAllTime ? 'none' : '1px solid rgba(0,0,0,0.04)'
            }}>
                {showTitle && (
                    <DateRangeIcon sx={{ color: '#6366f1', fontSize: 14 }} />
                )}
                <FormControlLabel
                    control={
                        <StyledSwitch
                            checked={isAllTime}
                            onChange={(e) => handleToggleAllTime(e.target.checked)}
                        />
                    }
                    label={
                        <Typography sx={{
                            fontSize: '9px',
                            fontWeight: 600,
                            color: isAllTime ? '#6366f1' : '#64748b',
                            fontFamily: 'Cairo',
                            mr: 0.3,
                            whiteSpace: 'nowrap'
                        }}>
                            {t('GLOBAL.all_time') || 'الكل'}
                        </Typography>
                    }
                    labelPlacement="start"
                    sx={{ m: 0 }}
                />
            </Box>

            {/* Date Pickers - Collapse when all_time */}
            <Collapse in={!isAllTime} timeout={200}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
                    {/* From Date */}
                    <DateTimeField>
                        <FieldLabel>{t('GLOBAL.from') || 'من'}</FieldLabel>
                        <DatePicker
                            selected={dateRange?.creationDateFrom || null}
                            onChange={(date) => handleDateChange('from', date)}
                            showTimeInput
                            dateFormat="yyyy-MM-dd HH:mm"
                            placeholderText={fromTitle || 'تاريخ البداية'}
                            customInput={<DateInputButton field="from" />}
                            customTimeInput={<CustomTimeInput />}
                            calendarClassName="datetime-pro-calendar"
                            popperProps={{ strategy: 'fixed' }}
                            popperPlacement="bottom-start"
                            onCalendarOpen={() => setFromFocused(true)}
                            onCalendarClose={() => setFromFocused(false)}
                            maxDate={dateRange?.creationDateTo || null}
                        />
                    </DateTimeField>

                    {/* Arrow */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.2 }}>
                        <ArrowDownwardIcon sx={{ fontSize: 14, color: '#cbd5e1' }} />
                    </Box>

                    {/* To Date */}
                    <DateTimeField>
                        <FieldLabel>{t('GLOBAL.to') || 'إلى'}</FieldLabel>
                        <DatePicker
                            selected={dateRange?.creationDateTo || null}
                            onChange={(date) => handleDateChange('to', date)}
                            showTimeInput
                            dateFormat="yyyy-MM-dd HH:mm"
                            placeholderText={toTitle || 'تاريخ النهاية'}
                            customInput={<DateInputButton field="to" />}
                            customTimeInput={<CustomTimeInput />}
                            calendarClassName="datetime-pro-calendar"
                            popperProps={{ strategy: 'fixed' }}
                            popperPlacement="bottom-start"
                            onCalendarOpen={() => setToFocused(true)}
                            onCalendarClose={() => setToFocused(false)}
                            minDate={dateRange?.creationDateFrom || null}
                        />
                    </DateTimeField>
                </Box>
            </Collapse>
        </GlassContainer>
    );
};

export default CustomDateRange;
